import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SettingsPanel from './SettingsPanel';
import './PhotoGrid.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function PhotoGrid({ user, onPhotoClick, onLogout, refreshTrigger }) {
    const [photos, setPhotos] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [albums, setAlbums] = useState([]);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadAlbumId, setUploadAlbumId] = useState('');
    const [uploadPublished, setUploadPublished] = useState(false);
    const [uploadIsCover, setUploadIsCover] = useState(false);
    const [storageUsage, setStorageUsage] = useState(null);

    useEffect(() => {
        fetchAlbums();
        fetchStorageUsage();
    }, []);

    useEffect(() => {
        fetchPhotos();
        fetchStorageUsage();
    }, [currentPage, selectedAlbum, refreshTrigger]);

    const fetchAlbums = async () => {
        try {
            const response = await axios.get(`${API_BASE}/albums`, {
                withCredentials: true
            });
            setAlbums(response.data);
            // Set default album as default selection
            const defaultAlbum = response.data.find(a => a.is_default);
            if (defaultAlbum && !uploadAlbumId) {
                setUploadAlbumId(defaultAlbum.id);
            }
        } catch (error) {
            console.error('Error fetching albums:', error);
        }
    };

    const fetchStorageUsage = async () => {
        try {
            const response = await axios.get(`${API_BASE}/storage/usage`, {
                withCredentials: true
            });
            setStorageUsage(response.data);
        } catch (error) {
            console.error('Error fetching storage usage:', error);
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const fetchPhotos = async () => {
        try {
            const url = selectedAlbum
                ? `${API_BASE}/photos?page=${currentPage}&albumId=${selectedAlbum.id}`
                : `${API_BASE}/photos?page=${currentPage}`;

            const response = await axios.get(url, {
                withCredentials: true
            });

            setPhotos(response.data.photos);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error('Error fetching photos:', error);
            setError('Failed to load photos');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setShowUploadModal(true);
        }
        e.target.value = null;
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('photo', selectedFile);
        formData.append('albumId', uploadAlbumId);
        formData.append('published', uploadPublished);
        formData.append('isCover', uploadIsCover);

        setUploading(true);
        setError('');

        try {
            await axios.post(`${API_BASE}/photos/upload`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Refresh the photo grid and storage usage
            setCurrentPage(1);
            fetchPhotos();
            fetchStorageUsage();
            setShowUploadModal(false);
            setSelectedFile(null);
            setUploadPublished(false);
            setUploadIsCover(false);
        } catch (error) {
            console.error('Upload error:', error);
            setError(error.response?.data?.error || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleAlbumChange = (e) => {
        const albumId = e.target.value;
        if (albumId === '') {
            setSelectedAlbum(null);
        } else {
            const album = albums.find(a => a.id === parseInt(albumId));
            setSelectedAlbum(album);
        }
        setCurrentPage(1);
    };

    const handleAlbumManagerChange = () => {
        fetchAlbums();
        fetchPhotos();
    };

    return (
        <div className="photo-grid-container">
            <header className="header">
                <h1>Photo Manager</h1>
                <div className="header-info">
                    <span>Welcome, {user.username}</span>
                    {storageUsage && (
                        <span className="storage-usage">
                            Storage: {formatBytes(storageUsage.totalBytes)} ({storageUsage.totalFiles} {storageUsage.totalFiles === 1 ? 'file' : 'files'})
                        </span>
                    )}
                    <button onClick={() => setShowSettings(true)} className="hamburger-btn" title="Settings">
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                    <button onClick={onLogout} className="logout-btn">Logout</button>
                </div>
            </header>

            <div className="controls-section">
                <div className="album-filter">
                    <label htmlFor="album-select">View Album:</label>
                    <select
                        id="album-select"
                        value={selectedAlbum?.id || ''}
                        onChange={handleAlbumChange}
                    >
                        <option value="">All Photos</option>
                        {albums.map(album => (
                            <option key={album.id} value={album.id}>
                                {album.name} {album.is_default ? '(Default)' : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="upload-section">
                    <label className="upload-btn">
                        Upload Photo
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                    </label>
                </div>
            </div>

            {error && <div className="error">{error}</div>}

            {photos.length === 0 ? (
                <div className="no-photos">
                    <p>No photos in this album yet. Upload your first photo!</p>
                </div>
            ) : (
                <>
                    <div className="photo-grid">
                        {photos.map(photo => (
                            <div
                                key={photo.id}
                                className="photo-card"
                                onClick={() => onPhotoClick(photo)}
                            >
                                <img
                                    src={`${API_BASE}/photos/${photo.id}/thumbnail`}
                                    alt={photo.title || photo.original_filename}
                                />
                                <div className="photo-info">
                                    <p className="photo-name">{photo.title || photo.original_filename}</p>
                                    <p className="photo-user">by {photo.username}</p>
                                    {photo.album_name && <p className="photo-album">Album: {photo.album_name}</p>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="pagination">
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

            {showUploadModal && (
                <div className="upload-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
                    <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Upload Photo</h3>
                        <p className="selected-file-name">{selectedFile?.name}</p>

                        <div className="form-group">
                            <label htmlFor="upload-album-select">Select Album:</label>
                            <select
                                id="upload-album-select"
                                value={uploadAlbumId}
                                onChange={(e) => setUploadAlbumId(e.target.value)}
                                disabled={uploading}
                            >
                                {albums.map(album => (
                                    <option key={album.id} value={album.id}>
                                        {album.name} {album.is_default ? '(Default)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={uploadPublished}
                                    onChange={(e) => setUploadPublished(e.target.checked)}
                                    disabled={uploading}
                                />
                                Published
                            </label>
                        </div>

                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={uploadIsCover}
                                    onChange={(e) => setUploadIsCover(e.target.checked)}
                                    disabled={uploading}
                                />
                                Set as Album Cover Photo
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button onClick={handleFileUpload} disabled={uploading}>
                                {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                            <button onClick={() => setShowUploadModal(false)} disabled={uploading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SettingsPanel
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onAlbumChange={handleAlbumManagerChange}
                user={user}
            />
        </div>
    );
}

export default PhotoGrid;
